/** @jsx jsx */
import { useEffect, useRef, useState, useMemo } from 'react'
import { useTimeoutFn, useUpdateEffect } from 'react-use'
import { ClientGameHandController } from '../../controllers/ClientGameHandController'
import { logger } from '../../utils/logger'
import { IGameData } from './useGameData'
import { Model } from '../../sharedBetweenServerAndClient__DO_NOT_EDIT/types'
import { IPositionProps } from './positionProps'

export const useGetController = (gameData: IGameData, positioning: IPositionProps) => {
  const { currentHand, currentUserHand } = gameData
  const previousHandRef = useRef<Model.GameHandUserData>()
  useEffect(() => {
    previousHandRef.current = { userHand: currentUserHand, gameHand: currentHand }
  }, [gameData.currentHand.id])
  const previousHandData = previousHandRef.current
  const [currentHandId, setCurrentHandId] = useState(currentHand.id)
  const [_isReady, cancel, reset] = useTimeoutFn(() => {
    logger.debug('timeout complete, setting new controller')
    setCurrentHandId(currentHand.id)
  }, 7500)
  useEffect(() => {
    cancel()
  }, [])
  useUpdateEffect(() => {
    logger.debug(
      `a new gameHand has been detected, deferring its usage while we animate away the old hand`,
      'GameDetailsContent',
      { currentHandId, newHandId: currentHand.id },
    )
    reset()
  }, [currentHand.id])
  const currentController = useMemo(() => {
    // There's a bug where sometimes, the userHand and the gameHand
    // don't match up so the game flashes the players older hands
    // We need to figure out the best way of matching this up
    const usedHand: Model.GameHandUserData =
      previousHandData && currentHand.id !== currentHandId
        ? {
            gameHand: gameData.gameHands.find(({ id }) => id === currentHandId)!,
            userHand: previousHandData.userHand,
          }
        : {
            gameHand: currentHand,
            userHand: currentUserHand,
          }
    return new ClientGameHandController(
      usedHand.gameHand,
      gameData.currentUserId,
      gameData.users,
      gameData.game,
      positioning,
      usedHand.userHand,
    )
  }, [gameData, currentHandId])
  const previousControllerRef = useRef<ClientGameHandController>()
  useEffect(() => {
    previousControllerRef.current = currentController
  }, [currentController])
  const previousController = previousControllerRef.current
  return { controller: currentController, previousController }
}
